import { getLoraPreview, useLoras } from "@features/loras/use-loras";
import { useNavigateAlt } from "@features/utils";
import {
  ArrowLeftIcon,
  CameraIcon,
  InformationCircleIcon,
  PencilIcon,
  PhotoIcon,
} from "@heroicons/react/16/solid";
import {
  Badge,
  Box,
  Flex,
  Heading,
  IconButton,
  Table,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { getPreviousPath, Page } from "@views/layout/layout";
import { formatDate } from "date-fns";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

export const MyModels = () => {
  const { t } = useTranslation();
  const { loras } = useLoras();
  const { site } = useParams();
  const navigateAlt = useNavigateAlt();

  // Filter out failed models older than 24 hours
  const filteredLoras = loras.data?.filter((lora) => {
    if (lora.status === "failed") {
      const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;
      return lora.created_at && lora.created_at > twentyFourHoursAgo;
    }
    return true;
  });

  return (
    <Page>
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        {/* Header */}
        <Flex align="center" gap="4" className="mb-6">
          <Link to={getPreviousPath()}>
            <ArrowLeftIcon className="h-5 w-5 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 cursor-pointer" />
          </Link>
          <Heading size="6">{t("my_models.title")}</Heading>
        </Flex>

        {/* Description */}
        <Text size="2" color="gray" className="mb-6 block">
          {t("my_models.description")}
        </Text>

        {/* Models Table */}
        <Box className="border rounded-lg overflow-hidden dark:border-slate-700">
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>
                  {t("my_models.table.name")}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>
                  {t("my_models.table.type")}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>
                  {t("my_models.table.status")}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>
                  {t("my_models.table.public")}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>
                  {t("my_models.table.created")}
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>
                  {t("my_models.table.actions", "Actions")}
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {_.sortBy(filteredLoras, "subtype", "name")?.map((lora) => (
                <Table.Row key={lora.id}>
                  <Table.Cell>
                    <Flex align="center" gap="2">
                      <img
                        src={getLoraPreview(lora.id, lora.preview_status)}
                        className="w-8 h-8 rounded-full object-cover"
                        alt={lora.name}
                      />
                      <Text weight="medium">
                        {lora.name}
                        {lora.technology === "images" &&
                          lora.subtype === "avatar" &&
                          " (LQ)"}
                      </Text>
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="soft" color="gray">
                      {lora.subtype || "model"}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Flex align="center" gap="1">
                      <Badge
                        variant="soft"
                        color={
                          lora.status === "completed"
                            ? "green"
                            : lora.status === "pending"
                            ? "orange"
                            : "red"
                        }
                      >
                        {lora.status || "unknown"}
                      </Badge>
                      {lora.status === "failed" && (
                        <Tooltip
                          content={t(
                            "my_models.failed_tooltip",
                            "Failed models are credited back automatically, please try again later"
                          )}
                        >
                          <InformationCircleIcon className="h-4 w-4 text-red-500 cursor-help" />
                        </Tooltip>
                      )}
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="soft" color={lora.public ? "blue" : "gray"}>
                      {lora.public
                        ? t("my_models.public")
                        : t("my_models.private")}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="1" color="gray">
                      {lora.created_at
                        ? formatDate(new Date(lora.created_at), "MMM d, yyyy")
                        : "-"}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Flex gap="2">
                      {/* Edit button */}
                      <Tooltip content={t("general.edit", "Edit model")}>
                        <IconButton
                          variant="soft"
                          color="gray"
                          onClick={(event) => {
                            console.log(
                              `/${site || "portraits"}/lora/${lora.id}`
                            );
                            navigateAlt(
                              `/${site || "portraits"}/lora/${lora.id}`,
                              event
                            );
                          }}
                        >
                          <PencilIcon className="w-3 h-3" />
                        </IconButton>
                      </Tooltip>
                      {/* Generate photo button */}
                      {lora.status === "completed" && (
                        <Tooltip
                          content={t(
                            "my_models.generate_photo",
                            "Generate photo"
                          )}
                        >
                          <IconButton
                            variant="soft"
                            color="blue"
                            onClick={(event) =>
                              navigateAlt(
                                `/${
                                  site || "portraits"
                                }/camera/new?mode=classic&model=${lora.id}`,
                                event
                              )
                            }
                          >
                            <CameraIcon className="w-3 h-3" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* See results button */}
                      {lora.status === "completed" && (
                        <Tooltip
                          content={t("my_models.see_results", "See results")}
                        >
                          <IconButton
                            variant="soft"
                            color="green"
                            onClick={(event) =>
                              navigateAlt(
                                `/${
                                  site || "portraits"
                                }/home/search?q=${encodeURIComponent(
                                  lora.name
                                )}`,
                                event
                              )
                            }
                          >
                            <PhotoIcon className="w-3 h-3" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}

              {(!filteredLoras || filteredLoras.length === 0) && (
                <Table.Row>
                  <Table.Cell colSpan={6}>
                    <Text
                      color="gray"
                      size="2"
                      className="text-center py-8 block"
                    >
                      {t("my_models.empty")}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table.Root>
        </Box>
      </div>
    </Page>
  );
};
